import React, { useEffect, useRef, useState, useCallback } from 'react';
import { UltravoxSession } from 'ultravox-client';
import useAppStore from '../../store/AppStore';
import sleep from '../../utils/common/sleep';

const UltravoxClient = ({
  joinUrl,
  onStatusChange,
  onTranscriptsUpdate,
  onSessionReady,
  onSessionDisconnect,
  onSessionError,
  autoJoin = false,
  enableTranscripts = true,
  audioContext,
  destinationNode
}) => {
  const sessionRef = useRef(null);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [transcripts, setTranscripts] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);
  // const audioContext = new AudioContext();
  // Get app store for potential integration with existing state management
  const { setIsUltravoxAgentNodeAvailable } = useAppStore();

  const interceptAgentAudio = useCallback(async (session) => {
    // Try to capture agent audio if destination node is provided
    if (destinationNode && audioContext && session) {
      try {
        console.log('[UltravoxClient] Setting up audio routing for agent voice:', {
          hasDestinationNode: !!destinationNode,
          hasAudioContext: !!audioContext,
          sessionId: session?.id || 'unknown'
        });
        
        // Wait a bit for the session to fully initialize
        // setTimeout(() => {
          try {
            // Try to wait for agentSourceNode to be available
            let initialTime = Date.now();
            while (!session.agentSourceNode) {
              await sleep(100);
              if ((Date.now() - initialTime) > 3000) {
                console.log('[UltravoxClient] Timeout waiting for agentSourceNode after 3 seconds');
                break;
              }
            }
            // Check if the session has the agentSourceNode (even though it's private)
            // We'll try to access it anyway since JavaScript allows it at runtime
            if (session.agentSourceNode) {
              console.log('[UltravoxClient] Found agentSourceNode, connecting to destination');
              session.agentSourceNode.connect(destinationNode);
              console.log('[UltravoxClient] Agent audio connected to custom destination');
            } else {
              console.log('[UltravoxClient] agentSourceNode not available yet, will try alternative approach');
              
              // Alternative approach: Create a script processor to monitor audio
              const scriptProcessor = audioContext.createScriptProcessor(4096, 1, 1);
              const analyser = audioContext.createAnalyser();
              analyser.fftSize = 2048;
              
              // Connect to the main destination to monitor audio
              scriptProcessor.connect(audioContext.destination);
              
              // Monitor for audio activity
              scriptProcessor.onaudioprocess = (event) => {
                console.log("Ultravox : event : ", event)
                // This will be called when audio is processed
                // We can use this to detect when agent is speaking
              };
              
              session.customAudioNodes = {
                scriptProcessor,
                analyser
              };
            }
            setIsUltravoxAgentNodeAvailable(true)
          } catch (err) {
            console.warn('Failed to connect agent audio:', err);
          }
        // }, 2000); // Wait 2 seconds for session to fully initialize
        
      } catch (err) {
        console.warn('Failed to set up audio routing:', err);
      }
    }
  }, [destinationNode, audioContext]);
  // Initialize Ultravox session
  const initializeSession = useCallback(() => {
    if (sessionRef.current) {
      // Clean up existing session
    //   sessionRef.current.leaveCall();
      sessionRef.current = null;
    }

    try {
      const session = new UltravoxSession({
        audioContext: audioContext,
      });
      sessionRef.current = session;

      // Set up event listeners
      session.addEventListener('status', (event) => {
        const status = session.status;
        setSessionStatus(status);
        setIsConnected(status === 'connected');
        
        console.log('[UltravoxClient] Session status changed:', {
          status,
          sessionId: session?.id || 'unknown',
          timestamp: new Date().toISOString()
        });
        
        // Notify parent component
        if (onStatusChange) {
          onStatusChange(status, session);
        }

        // Handle different status states
        switch (status) {
          case 'disconnected':
            setIsConnected(false);
            setError(null);
            if (onSessionDisconnect) {
              onSessionDisconnect(session);
            }
            break;
          case 'connecting':
            setError(null);
            break;
          case 'connected':
          case 'idle':
            setIsConnected(true);
            setError(null);
            console.log("[UltravoxClient] Ultravox Connected successfully");
            
            // Try to intercept agent audio now that session is connected
            interceptAgentAudio(session);
            
            if (onSessionReady) {
              onSessionReady(session);
            }
            break;
          case 'error':
            setIsConnected(false);
            setError('Session connection error');
            if (onSessionError) {
              onSessionError('Session connection error', session);
            }
            break;
          default:
            break;
        }
      });

      if (enableTranscripts) {
        session.addEventListener('transcripts', (event) => {
          const newTranscripts = session.transcripts;
          setTranscripts(newTranscripts);
          
          // console.log('Ultravox transcripts updated:', newTranscripts);
          
          // Notify parent component
          if (onTranscriptsUpdate) {
            onTranscriptsUpdate(newTranscripts, session);
          }
        });
      }

      return session;
    } catch (err) {
      console.error('[UltravoxClient] Failed to initialize Ultravox session:', {
        error: err.message,
        stack: err.stack,
        timestamp: new Date().toISOString()
      });
      setError(err.message);
      if (onSessionError) {
        onSessionError(err.message, null);
      }
      return null;
    }
  }, [onStatusChange, onTranscriptsUpdate, onSessionReady, onSessionError, enableTranscripts]);

  // Join call function
  const joinCall = useCallback(async (url = joinUrl) => {
    if (!url) {
      const errorMsg = 'No join URL provided';
      setError(errorMsg);
      if (onSessionError) {
        onSessionError(errorMsg, sessionRef.current);
      }
      return false;
    }

    try {
      if (!sessionRef.current) {
        initializeSession();
      }

      if (sessionRef.current) {
        await sessionRef.current.joinCall(url);
        console.log('[UltravoxClient] Joined Ultravox call successfully:', {
          url,
          sessionId: sessionRef.current?.id || 'unknown',
          timestamp: new Date().toISOString()
        });
        return true;
      }
    } catch (err) {
      console.error('[UltravoxClient] Failed to join Ultravox call:', {
        error: err.message,
        url,
        stack: err.stack,
        timestamp: new Date().toISOString()
      });
      setError(err.message);
      if (onSessionError) {
        onSessionError(err.message, sessionRef.current);
      }
    }
    return false;
  }, [joinUrl, initializeSession, onSessionError]);

  // Leave call function
  const leaveCall = useCallback(() => {
    try {
      if (sessionRef.current) {
        sessionRef.current.leaveCall();
        console.log('[UltravoxClient] Left Ultravox call successfully:', {
          sessionId: sessionRef.current?.id || 'unknown',
          timestamp: new Date().toISOString()
        });
        setIsConnected(false);
        setSessionStatus('disconnected');
      }
    } catch (err) {
      console.error('[UltravoxClient] Failed to leave Ultravox call:', {
        error: err.message,
        stack: err.stack,
        timestamp: new Date().toISOString()
      });
      setError(err.message);
    }
  }, []);

  // Expose session methods to parent component
  const getSession = useCallback(() => {
    return sessionRef.current;
  }, []);

  // Initialize session on mount
  useEffect(() => {
    console.log('[UltravoxClient] Initializing session on component mount');
    initializeSession();
    
    // Cleanup on unmount only
    return () => {
      if (sessionRef.current) {
        try {
          sessionRef.current.leaveCall();
        } catch (err) {
          console.warn('Error during cleanup leaveCall:', err);
        }
        sessionRef.current = null;
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Separate effect for auto-join when URL or autoJoin changes
  useEffect(() => {
    if (autoJoin && joinUrl && sessionRef.current) {
      joinCall(joinUrl);
    }
  }, [autoJoin, joinUrl]); // Removed joinCall from dependencies

  // This effect is now handled above, so removing this duplicate

  // Expose methods to parent via ref (if needed)
  React.useImperativeHandle(sessionRef, () => ({
    joinCall,
    leaveCall,
    getSession,
    isConnected,
    sessionStatus,
    transcripts,
    error
  }));

  // This component doesn't render anything visible
  // It's a headless component that manages the Ultravox session
  return null;
};

// Export both the component and some utility functions
export default UltravoxClient;

// Utility hook for easier integration
export const useUltravoxClient = (config = {}) => {
  const sessionRef = useRef(null);
  const [status, setStatus] = useState(null);
  const [transcripts, setTranscripts] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState(null);

  const handleStatusChange = useCallback((newStatus, session) => {
    setStatus(newStatus);
    setIsConnected(newStatus === 'connected');
    if (config.onStatusChange) {
      config.onStatusChange(newStatus, session);
    }
  }, [config]);

  const handleTranscriptsUpdate = useCallback((newTranscripts, session) => {
    setTranscripts(newTranscripts);
    if (config.onTranscriptsUpdate) {
      config.onTranscriptsUpdate(newTranscripts, session);
    }
  }, [config]);

  const handleSessionError = useCallback((errorMsg, session) => {
    setError(errorMsg);
    if (config.onSessionError) {
      config.onSessionError(errorMsg, session);
    }
  }, [config]);

  const joinCall = useCallback((url) => {
    if (sessionRef.current) {
      return sessionRef.current.joinCall(url);
    }
    return Promise.reject(new Error('Session not initialized'));
  }, []);

  const leaveCall = useCallback(() => {
    if (sessionRef.current) {
      sessionRef.current.leaveCall();
    }
  }, []);

  const getSession = useCallback(() => {
    return sessionRef.current?.getSession();
  }, []);

  return {
    sessionRef,
    status,
    transcripts,
    isConnected,
    error,
    joinCall,
    leaveCall,
    getSession,
    handleStatusChange,
    handleTranscriptsUpdate,
    handleSessionError
  };
};
